<script setup>
import { generatePassword } from '@/shared/utils/helpers.js';
import CFInputEyeToggle from '@/shared/components/CFInputEyeToggle.vue';
import { computed, ref, useId } from 'vue';
import CFInputError from '@/shared/components/CFInputError.vue';
import { Tooltip as VueTooltip } from 'floating-vue';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import CFInlineSpinner from '@/ui/Spinner/CFInlineSpinner.vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: '',
    },
    id: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    labelClass: {
        type: String,
        default: '',
    },
    inputClass: {
        type: String,
        default: '',
    },
    errors: {
        type: [Boolean, String],
        default: false,
    },
    position: {
        type: String,
        default: 'inset',
        validator: (v) => ['inset', 'horizontal'].includes(v),
    },
    name: {
        type: String,
        default: null,
    },
    type: {
        type: String,
        default: 'text',
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    placeholder: {
        type: String,
        default: null,
    },
    autocomplete: {
        type: String,
        default: null,
    },
    tooltip: {
        type: String,
        default: null,
    },
    showGenerate: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    class: {
        type: String,
        default: null,
    },
});

const emits = defineEmits(['update:modelValue', 'blur']);

const inputRef = ref(null);

const inputId = props.id ?? useId();

const inputType = ref(props.type);

const inputValue = computed(() => {
    if (props.type === 'date' && props.modelValue !== '') {
        const result = props.modelValue?.match(/^(\d{4}-\d{2}-\d{2})T?/) ?? '';
        return result[1] ?? '';
    }

    return props.modelValue;
});

const generateRandomPassword = () => {
    emits('update:modelValue', generatePassword());
};

const showPassword = () => {
    inputType.value = inputType.value === 'password' ? 'text' : 'password';
};

const focus = () => {
    if (!props.readonly) {
        inputRef.value.focus();
    }
};

defineExpose({
    focus,
});
</script>

<template>
    <div
        :class="[
            {
                'mt-2.5': position === 'horizontal',
            },
            props.class,
        ]"
    >
        <div
            :class="{
                'flex h-[50px] flex-col justify-center border-b': position === 'inset',
                'border-neutrals-500 bg-grey-light': position === 'inset' && readonly,
                'bg-white focus-within:border-b focus-within:border-b-black': position === 'inset' && !readonly,
                'border-transparent': position === 'inset' && !errors && !readonly,
                'border-red-500': position === 'inset' && errors,
                'flex items-start': position === 'horizontal',
            }"
        >
            <label
                v-if="position === 'horizontal'"
                :for="inputId"
                class="text-xs font-bold text-gray-900"
                :class="[
                    labelClass,
                    {
                        'text-2xs block leading-none': position === 'inset',
                        'flex h-[50px] items-center pr-2.5 text-xs': position === 'horizontal',
                        'w-[140px]': position === 'horizontal' && !labelClass,
                    },
                ]"
            >
                {{ label ?? placeholder }}
            </label>
            <div
                :class="[
                    inputClass,
                    {
                        'flex-1': position === 'horizontal' && !inputClass,
                    },
                ]"
            >
                <div class="relative">
                    <input
                        v-bind="$attrs"
                        :id="inputId"
                        ref="inputRef"
                        :type="inputType"
                        :name="name"
                        :autocomplete="autocomplete"
                        :readonly="readonly || loading"
                        placeholder=" "
                        :value="inputValue"
                        class="text-primary block w-full rounded-none text-sm"
                        :class="{
                            'border-neutrals-500 bg-grey-light border': readonly,
                            'h-[50px] focus:ring-0 focus:outline-hidden': position === 'horizontal',
                            'border-b-warning': errors && position !== 'inset',
                            'focus:border-b-primary border-b border-transparent focus:border-b focus:border-transparent':
                                position === 'horizontal' && !readonly,
                            'focus:border-neutrals-500': position === 'horizontal' && readonly,
                            'cursor-default': readonly,
                            'peer focus:border-primary appearance-none border-0 px-3 focus:ring-0 focus:outline-hidden':
                                position === 'inset',
                            'pt-5': position === 'inset' && label,
                            'bg-white': position === 'inset' && !readonly,
                        }"
                        @input="emits('update:modelValue', $event.target.value)"
                        @blur="emits('blur', $event)"
                    />
                    <label
                        v-if="position === 'inset'"
                        :for="inputId"
                        class="text-primary peer-placeholder-shown:text-neutrals-600 peer-focus:text-primary pointer-events-none absolute start-3 top-3.5 z-10 origin-left -translate-y-4 scale-75 transform text-sm font-bold duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:font-normal peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-bold"
                    >
                        {{ label ?? placeholder }}
                    </label>
                    <span
                        v-if="loading"
                        role="status"
                        class="absolute z-10 flex items-center"
                        :class="{
                            'top-0 right-0.5': position === 'inset',
                            'top-4 right-3': position === 'horizontal',
                        }"
                    >
                        <CFInlineSpinner size="sm" />
                        <span class="sr-only">{{ $t('general.loading') }}...</span>
                    </span>

                    <div
                        v-if="tooltip"
                        class="group absolute inset-y-0 right-2 flex w-max items-stretch"
                    >
                        <VueTooltip class="flex cursor-help items-center">
                            <div class="px-2">
                                <CFIcon
                                    name="question-circle"
                                    :size="16"
                                    class="text-gray-500"
                                />
                            </div>

                            <template #popper>
                                {{ tooltip }}
                            </template>
                        </VueTooltip>
                    </div>

                    <button
                        v-if="showGenerate"
                        type="button"
                        class="absolute inset-y-0 right-[50px] flex cursor-pointer items-center text-xs underline underline-offset-3"
                        @click="generateRandomPassword"
                    >
                        {{ $t('general.generate_password') }}
                    </button>

                    <CFInputEyeToggle
                        v-if="type === 'password'"
                        class="mr-3 stroke-black"
                        :condition="inputType === 'password'"
                        @click="showPassword"
                    />
                </div>

                <template v-if="position === 'horizontal' && typeof errors === 'string' && errors !== ''">
                    <CFInputError :message="errors" />
                </template>
            </div>
        </div>

        <template v-if="position === 'inset' && typeof errors === 'string' && errors !== ''">
            <CFInputError :message="errors" />
        </template>
    </div>
</template>
